import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { Icon } from '@captaincause/ui/Icon'
import { SocialMedia } from '@captaincause/ui/SocialMedia'
import LogoWhite from 'assets/dift-full-white.svg'
import laMission from 'assets/la-mission.webp'
import CallToAction from 'blocks/CallToAction'
import { Button } from 'components/Button'

interface FooterLinks {
  title: string
  links: {
    href: string
    title: string
    locale?: false | undefined
  }[]
}

function Links({ title, links }: FooterLinks) {
  return (
    <div className="md:min-w-[320px] md:flex-[6] lg:min-w-[unset]">
      <div className="mb-4 text-center font-medium lg:text-left">{title}</div>
      <ul className="space-y-4 text-center text-white/80 lg:text-left">
        {links.map(link => (
          <li key={link.title}>
            <Button
              variant="raw"
              className="w-full lg:justify-start"
              href={link.href}
              locale={link.locale}
            >
              {link.title}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Footer({ hideFooterCta = false }: { hideFooterCta?: boolean }) {
  const { t } = useTranslation('footer.component')

  return (
    <>
      {/* TODO: Connect the footer to a field from strapi to enable/disable the top-footer */}
      {hideFooterCta ? null : (
        <CallToAction
          title={t('top.title')}
          link={{
            url: 'https://app.dift.com/c/campaigns/new',
            title: t('top.link'),
          }}
        />
      )}
      <footer
        id="footer"
        data-testid="footer"
        className="w-full bg-black px-8 py-14 text-light-beige"
      >
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col items-center lg:flex-[3] lg:items-start">
            <Button
              href="/"
              variant="raw"
              className="mb-4 block h-auto w-24 lg:mb-6"
            >
              <Image
                src={LogoWhite}
                alt="Dift"
                style={{ width: 'inherit', height: 'inherit' }}
              />
            </Button>
            <Button
              href="/blog/la-mission-coulisses-de-captain-cause"
              className="mb-6 mt-4 flex items-center p-0 text-center text-xl font-medium lg:mb-6 lg:text-left"
              locale={false}
            >
              {t('title1')}
              <br />
              {t('title2')}
              <br />
              <div className="mt-2 flex items-center">
                <Image
                  src={laMission.src}
                  alt="La Mission"
                  height={24}
                  width={140}
                  className="inline-block h-auto w-[140px] p-0"
                />
                <Icon icon="arrow-go" className="ml-2" />
              </div>
            </Button>
            <SocialMedia
              id="socials-media-footer"
              className="lg:align-items mb-4 mt-2 lg:mb-0"
              iconSize="normal"
              socialsMedia={[
                {
                  href: 'https://www.linkedin.com/company/dift-com/',
                  icon: 'linkedin',
                  label: t('linkedin-label'),
                },
                {
                  href: 'https://www.youtube.com/channel/UC1rcA68A868I-gVHXjJuwPA',
                  icon: 'youtube',
                  label: t('youtube-label'),
                },
                {
                  href: 'https://www.instagram.com/dift_com/',
                  icon: 'instagram',
                  label: t('instagram-label'),
                },
              ]}
            />
          </div>
          <div className="flex w-full flex-col flex-wrap items-center space-y-10 md:flex-row lg:flex-[9] lg:flex-row lg:items-start lg:justify-between lg:space-y-0">
            <Links
              title={t('company')}
              links={[
                {
                  title: t('team'),
                  href: '/team',
                },
                {
                  title: t('jobs'),
                  href: 'https://dift.com/jobs',
                  locale: false,
                },
                {
                  title: t('dotation'),
                  href: 'https://captaincause.org',
                  locale: false,
                },
                {
                  title: t('about'),
                  href: '/about',
                  locale: false,
                },
              ]}
            />
            <Links
              title={t('ressources')}
              links={[
                {
                  title: t('press'),
                  href: 'https://dift.notion.site/Espace-presse-Captain-Cause-65c0131ddc3744b39d72e516b936ed27',
                  locale: false,
                },
                {
                  title: 'Blog',
                  href: '/blog',
                  locale: false,
                },
                {
                  title: 'Contact',
                  href: '/contact',
                },
                {
                  title: 'FAQ',
                  href: '/faq',
                },
              ]}
            />
            <Links
              title={t('legal2')}
              links={[
                {
                  title: t('legal'),
                  href: '/legal',
                  locale: false,
                },
                {
                  title: t('privacy'),
                  href: '/privacy',
                  locale: false,
                },
                {
                  title: t('cgu-cc'),
                  href: '/cgu',
                  locale: false,
                },
              ]}
            />
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-[1400px] justify-center pt-8 text-xs lg:justify-start">
          © Dift (by Captain Cause), {t('rights')}.
        </div>
      </footer>
    </>
  )
}
